import { Boss, BossDef, BossAttack } from './boss';
import { GAME, ASSETS } from '../config';
import { TIER2_BOSS_POOL } from './boss-tier2-pool';
import { BOSS_TIER3_POOL } from './boss-tier3-pool';
import { BOSS_EXTRA_POOL } from './boss-extra-pool';
import {
  advanceBossPattern,
  SHU_MO_PATTERN,
  SHU_MO_MOVE_SNIPER,
  SHU_MO_MOVE_LASER,
  SHU_MO_MOVE_CROSS_LASER,
  SHU_MO_MOVE_RING,
} from '../ai/boss-pattern';

/**
 * boss-bullet-hell (老闆 2026-05-31「子彈很多一次發個 20 顆」):
 * 產生對稱、均勻分布的扇形角度偏移陣列 (radians),中央對稱於 0。
 * fan-shot 的 angles 是相對「正下」的偏移,場景會 remap 成朝左 (π+offset);
 * 這裡只負責生出 N 個等距偏移,讓「一次 20 顆」彈幕容易組裝。
 * 純遊戲機制(學習與遊戲分離:不寫回修煉值)。
 *
 * @param count          子彈數 (≥1;老闆要的 20)
 * @param halfSpreadRad  半展開角 (rad),總寬 = 2*halfSpreadRad
 */
export function makeFanAngles(count: number, halfSpreadRad: number): number[] {
  const n = Math.max(1, Math.floor(count));
  if (n === 1) return [0];
  const out: number[] = [];
  const step = (halfSpreadRad * 2) / (n - 1);
  for (let i = 0; i < n; i++) out.push(-halfSpreadRad + step * i);
  return out;
}

/**
 * boss-dodgeable (老闆 2026-05-31 試玩回饋:「魔王的攻擊應該要有地方可以躲,
 * 你這個完全沒辦法躲」):
 *
 * 原 `makeFanAngles(20, ...)` 把 20 顆密集子彈正對玩家中心(瞄準方向),中央間隔
 * 僅 ~3°,9 歲玩家「往哪移下一波就重新瞄準」→ 彈牆永遠正對他,實質無縫。
 *
 * 這個 helper 產生「上下兩束 + 中央安全縫」的對稱扇形:在瞄準方向(offset 0)
 * 周圍 ±gapRad/2 的弧度內**完全不放彈**,玩家一眼看得出缺口、移過去就能躲。
 * 兩束各自仍均勻分布,維持「一次很多顆」的氣勢(老闆要的壓迫感)。
 *
 * 幾何(K-3 可躲性證明):
 *   - 場景把每個 offset remap 成 `π + offset`(朝左/朝玩家)。
 *   - 中央缺口 gapRad 是「角寬」。boss 距玩家約 600px(boss x≈680, 玩家 x≈80),
 *     角寬 θ 在該距離投影的垂直安全帶 ≈ 600 * tan(θ/2) * 2。
 *   - 預設 gapRad=0.7rad(40°)→ 安全帶 ≈ 600*tan(0.35)*2 ≈ 437px,
 *     遠大於玩家體積(~35px)、幾乎是整個可移動高度(480px),縫超級明顯。
 *
 * 純遊戲機制(學習與遊戲分離:不寫回修煉值)。
 *
 * @param count          子彈總數 (上下束平分;老闆要的 ~20)
 * @param halfSpreadRad  半展開角 (rad),總寬 = 2*halfSpreadRad
 * @param gapRad         中央安全縫「角寬」(rad);此弧度內(對稱於 0)不放彈
 */
export function makeFanAnglesWithGap(
  count: number,
  halfSpreadRad: number,
  gapRad: number,
): number[] {
  const n = Math.max(1, Math.floor(count));
  if (n === 1) return [0];
  const halfGap = Math.max(0, gapRad) / 2;
  // gap 退化成 0 或束角寬非正 → 回退一般對稱扇形(仍可躲,只是沒中央縫)。
  if (halfGap <= 0 || halfGap >= halfSpreadRad) return makeFanAngles(n, halfSpreadRad);

  // 上下兩束各放一半的子彈。每束從 halfGap 起、到 halfSpreadRad 止,均勻分布。
  const perSide = Math.floor(n / 2);
  const out: number[] = [];
  if (perSide === 1) {
    // 單顆 → 放在束的外緣,讓中央縫最大。
    out.push(-halfSpreadRad, halfSpreadRad);
  } else if (perSide >= 2) {
    const step = (halfSpreadRad - halfGap) / (perSide - 1);
    for (let i = 0; i < perSide; i++) {
      const mag = halfGap + step * i;
      out.push(-mag); // 上束(瞄準方向上方)
      out.push(mag);  // 下束(瞄準方向下方)
    }
  }
  // n 為奇數時多 1 顆:放最外緣(不破壞中央縫)。
  if (n % 2 === 1) out.push(halfSpreadRad);
  out.sort((a, b) => a - b);
  return out;
}

/**
 * boss-dodgeable: 中央安全縫的「角寬」(rad),三隻關底魔王共用。
 * 0.7rad ≈ 40°,在 boss→玩家 ~600px 距離投影出 ~430px 垂直安全帶,
 * 9 歲玩家一眼看得出、移過去就能躲(寧可偏簡單)。
 */
export const FAN_SAFE_GAP_RAD = 0.7;

/**
 * combat-polish (老闆 2026-06「ring 太陽春,一眼看穿、太簡單」):
 * berserk 環形大招的「進階參數」。原本 20 顆均勻單層環,間隔規律一眼看穿。
 * 改成【雙層 + 明顯缺口 + 緩慢旋轉】:
 *   - 雙層 (RING_BERSERK_LAYERS=2):外層角度錯開半步,玩家不能一條線穿兩層,要找對縫。
 *   - 缺口 (RING_BERSERK_GAP_RAD≈0.9rad/52°):整環缺一段,玩家衝缺口就安全 (縫永遠在)。
 *   - 旋轉 (RING_BERSERK_ROTATE_DPS):彈幕緩慢旋轉,看穿更難但徑向速度不變仍閃得過。
 * 速度維持 150~170 (K-3 鐵則:閃得過)。純遊戲機制 (學習與遊戲分離)。
 */
export const RING_BERSERK_LAYERS = 2;
export const RING_BERSERK_GAP_RAD = 0.9;
export const RING_BERSERK_ROTATE_DPS = 35;

/** 一顆環彈的計算結果 (純資料,場景據此 spawn)。vx/vy 已含旋轉切向分量。 */
export interface RingShot {
  angle: number;
  vx: number;
  vy: number;
  /** 該顆所屬層 (0=內層)。 */
  layer: number;
}

/**
 * combat-polish: 計算進階環形彈幕的每顆「角度 + 速度向量」(純函式,可 headless 測)。
 * 場景 _fireBossRing 直接用這個結果 spawn,確保「雙層/缺口/旋轉」邏輯有單元測試覆蓋。
 *
 * 規則:
 *   - layers 層同心環,每層 perLayer 顆均勻分布;外層整體錯開半步 (找對縫)。
 *   - 外層速度略快 (×1.18^layer) 讓層拉開不黏。
 *   - gapRad>0:角度正規化到 [-π,π] 後落在 [-gap/2, gap/2] 內的彈被略過 (safe-gap)。
 *   - rotateDps≠0:每顆加切向分量 (perpendicular),做出旋轉感 (徑向速度不變仍可躲)。
 *
 * @param perLayer  每層子彈數 (老闆要的 ~20)
 * @param speed     基礎徑向速度 px/秒
 * @param opts      layers / gapRad / rotateDps
 */
export function computeRingShots(
  perLayer: number,
  speed: number,
  opts?: { layers?: number; gapRad?: number; rotateDps?: number },
): RingShot[] {
  const per = Math.max(1, Math.floor(perLayer));
  const layers = Math.max(1, Math.floor(opts?.layers ?? 1));
  const halfGap = Math.max(0, opts?.gapRad ?? 0) / 2;
  const rotRadPerSec = ((opts?.rotateDps ?? 0) * Math.PI) / 180;
  const out: RingShot[] = [];
  for (let layer = 0; layer < layers; layer++) {
    const step = (Math.PI * 2) / per;
    const layerOffset = layer * (step / 2);
    const layerSpeed = speed * (1 + layer * 0.18);
    for (let i = 0; i < per; i++) {
      const angle = i * step + layerOffset;
      if (halfGap > 0) {
        let a = angle % (Math.PI * 2);
        if (a > Math.PI) a -= Math.PI * 2;
        if (a < -Math.PI) a += Math.PI * 2;
        if (Math.abs(a) < halfGap) continue;
      }
      let vx = Math.cos(angle) * layerSpeed;
      let vy = Math.sin(angle) * layerSpeed;
      if (rotRadPerSec !== 0) {
        const tang = rotRadPerSec * layerSpeed * 0.25;
        vx = Math.cos(angle) * layerSpeed - Math.sin(angle) * tang;
        vy = Math.sin(angle) * layerSpeed + Math.cos(angle) * tang;
      }
      out.push({ angle, vx, vy, layer });
    }
  }
  return out;
}

const SHU_MO: BossDef = {
  id: 'shu-mo',
  displayName: '數魔・乘九',
  hp: 1000,
  speedPxPerSec: 40,
  // boss-behavior: 魔王更大更有壓迫感 96→140 (×1.46). 800x600 場景 ~140px 合理.
  size: 140,
  color: 0x6a1b9a,
  assetKey: ASSETS.bossShuMo,
  shape: 'diamond',
  faction: 'celestial',
  affinityFaction: 'celestial', // 數魔 = math = star calc = celestial
  // boss-dodgeable: attackA 1500→1800 給玩家走位喘息(20 顆扇彈走 attackA 節奏);
  // 即使 berserk ×0.66 也有 ~1190ms 間隔重新找縫.
  attackACooldownMs: 1800,
  attackBCooldownMs: 4000,
  phase2HpThreshold: 400,
  attackBWarningMs: 800,
  introDialog: '計算我的數,凡人...',
  arenaTint: 0x1a0033,
  entranceCinematic: 'descend',
  deathCinematic: 'fragment-into-pieces',
  uniqueGimmick: 'echo-attacks',
  tickAttacks(self: Boss, nowMs: number, playerX: number): BossAttack[] {
    // learnable-boss-proto (老闆 2026-06「魔王要可學習、可精通、有攻防節奏」):
    //   SHU_MO 數魔不再「cooldown 到了就放招」(不可預測),改成**固定攻防循環**:
    //     IDLE(0.8s) → TELEGRAPH(0.7s 專屬預警) → FIRE(放招) → RECOVER(1.2s 破綻) → 下一招
    //   固定序列:狙擊 → 狙擊 → 雷射 (循環);玩家死幾次能「背起來舞步」。
    //   berserk 換成更密的序列 + 縮短 timing (仍固定可背,不是變隨機)。
    //   只在 justFired 那一幀真正吐出 BossAttack[];其餘幀只更新 telegraph 視覺狀態。
    //   獨佔招式: laser-beam (另兩隻不放雷射)。學習與遊戲分離:純遊戲機制。
    if (self.y < self.def.size / 2) return [];

    // 從 nowMs 推導本幀 dtMs (tickAttacks 沒拿到 dt)。首次 (-1) 用一幀近似避免巨跳。
    const dtMs = self._lastPatternTickMs < 0 ? 16 : Math.max(0, nowMs - self._lastPatternTickMs);
    self._lastPatternTickMs = nowMs;

    const berserk = self.berserkMode; // <=50% HP
    const tick = advanceBossPattern(self.patternState, dtMs, SHU_MO_PATTERN, berserk);

    // 把 tick 快照寫進 boss,供 scene 畫「招式專屬預警 + 破綻」視覺。
    self.patternTelegraph = {
      phase: tick.phase,
      moveId: tick.currentMoveId,
      telegraphProgress: tick.telegraphProgress,
      recoverProgress: tick.recoverProgress,
      vulnerable: tick.vulnerable,
    };

    // 只有「剛從 TELEGRAPH→FIRE」那一幀才真正放招。其餘 (IDLE/TELEGRAPH/RECOVER)
    // 回空陣列 → RECOVER 期間 boss 不放招 = 玩家的破綻輸出窗。
    if (!tick.justFired) return [];
    return shuMoMoveToAttacks(tick.currentMoveId, self, playerX, berserk);
  },
};

/**
 * learnable-boss-proto: 把 SHU_MO 序列裡的「招式 id」翻譯成實際 BossAttack[]。
 * 抽成獨立 export 純函式 → 序列→招式 的映射可單元測試 (不需驅動整個狀態機)。
 *
 *   'sniper' → 高速精準單發狙擊 (好預判、移動即可閃;體現「精準爆發」)。
 *   'laser'  → 招牌雷射光 (長預警再開火,角度開火當下鎖定 → K-3 可躲)。
 * berserk 時雷射補一道 (上下分開預警),壓迫感更大但每道都有預警縫。
 *
 * boss-phase2-transform: phase2 (berserk) 序列新增兩個 phase1 沒有的招式 id,
 * 讓「攻擊方式質變」(老闆「攻擊方式要改變 動態變換」):
 *   'cross-laser' → 雙雷射「交叉」(各偏一邊預警,玩家站中央縫即安全) — 新彈幕形態。
 *   'ring'        → 環形彈幕 (ring-burst,雙層 + 缺口 + 緩旋),衝缺口可躲 — 全新 kind。
 */
export function shuMoMoveToAttacks(
  moveId: string,
  self: Boss,
  playerX: number,
  berserk: boolean,
): BossAttack[] {
  const out: BossAttack[] = [];
  if (moveId === SHU_MO_MOVE_SNIPER) {
    const angle = Math.atan2(playerX - self.x, GAME.height * 0.85 - self.y);
    out.push({ kind: 'single-shot', angleRad: angle, speedPxPerSec: 420, damage: 14 });
  } else if (moveId === SHU_MO_MOVE_LASER) {
    // 雷射:長預警 (700ms) 讓 9 歲看清方向先閃,再開火 450ms。角度開火當下鎖定。
    out.push({ kind: 'laser-beam', warnMs: 700, fireMs: 450, thickness: 26, damage: 16 });
    if (berserk) {
      // berserk:再補一道雷射,壓迫感最大但每道都有預警縫。
      out.push({ kind: 'laser-beam', warnMs: 600, fireMs: 400, thickness: 22, damage: 14 });
    }
  } else if (moveId === SHU_MO_MOVE_CROSS_LASER) {
    // phase2 限定:雙雷射交叉 — 兩道各自長預警 (>=600ms) 同時警示,玩家看清「兩道往哪」
    // 站中間縫即安全。比單雷射壓迫感大,但仍是「看預警→站縫」可躲 (K-3 鐵則)。
    out.push({ kind: 'laser-beam', warnMs: 650, fireMs: 420, thickness: 22, damage: 14 });
    out.push({ kind: 'laser-beam', warnMs: 650, fireMs: 420, thickness: 22, damage: 14 });
  } else if (moveId === SHU_MO_MOVE_RING) {
    // phase2 限定:環形彈幕 (全新 kind) — 雙層 + 明顯缺口 + 緩旋,衝缺口就安全。
    // 速度維持 150 (K-3 閃得過);數魔半血才解鎖,phase1 從不放環。
    out.push({
      kind: 'ring-burst', count: 20, speedPxPerSec: 150, damage: 8,
      layers: RING_BERSERK_LAYERS, gapRad: RING_BERSERK_GAP_RAD, rotateDps: RING_BERSERK_ROTATE_DPS,
    });
  }
  return out;
}

const FU_HUN: BossDef = {
  id: 'fu-hun',
  displayName: '符魂・舌劍',
  hp: 1250,
  speedPxPerSec: 30,
  // boss-behavior: 96→144 (×1.5).
  size: 144,
  color: 0xad1457,
  assetKey: ASSETS.bossFuHun,
  shape: 'double-circle',
  faction: 'runic',
  affinityFaction: 'runic', // 符魂 = runic seal boss = runic
  attackACooldownMs: 2000,
  attackBCooldownMs: 6000,
  phase2HpThreshold: 500,
  attackBWarningMs: 800,
  introDialog: '舌劍封印,你的咒語無效!',
  arenaTint: 0x2d0a14,
  entranceCinematic: 'rise-from-ground',
  deathCinematic: 'crystal-shatter',
  uniqueGimmick: 'phase-shield',
  tickAttacks(self: Boss, nowMs: number, _playerX: number): BossAttack[] {
    // boss-movesets 差異化 (老闆「每一魔王招式要不一樣」):
    //   FU_HUN 符魂 = 符文/防禦控制 → 主軸【緩速 + 沉默 + 魔法彈幕】。控場為王。
    //   phase1: 魔法彈幕 + 緩速;phase2: 沉默 + 緩速 + 魔法鋪面;berserk: 環形。
    //   不放雷射 (那是 SHU_MO 招牌,它是控制者)。獨佔招式: slow-player (緩速)、
    //   重度 magic-bolt 鋪面。緩速非定身 (老闆鐵則:不要太挫折)。純遊戲機制。
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;
    const hpFrac = self.hpMax > 0 ? self.hp / self.hpMax : 1;
    const half = hpFrac <= 0.6;
    // attack-A:常駐魔法彈幕 (波浪魔法彈,視覺紫藍,跟直線彈明顯不同) + 偶爾緩速。
    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      // 魔法彈:波浪前進、速度適中 (200),數量隨血量增加製造控場壓力。
      out.push({ kind: 'magic-bolt', count: half ? 5 : 3, speedPxPerSec: 200, amplitudePx: 46, periodMs: 900, damage: 9 });
      if (half) {
        // 半血後加緩速 (×0.5, 1.5s) — 命中才緩、非定身,玩家仍可走位。
        out.push({ kind: 'slow-player', durationMs: 1500, slowMult: 0.5 });
      }
    }
    // attack-B:phase2 控場大招 — 沉默 + 緩速 + 魔法彈鋪面 (寬扇魔法感)。
    // boss-phase2-transform (老闆「攻擊方式要改變 動態變換」):phase2 質變 —
    //   除既有控場外,新增 fan-shot 寬扇彈幕 (phase1 從不放扇彈),中央留安全縫,
    //   讓玩家明顯感覺符魂「換成符陣鋪面」打法,不只是放更多魔法彈。
    if (self.phase === 2 && nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;
      out.push({ kind: 'silence-player', durationMs: 500 });
      out.push({ kind: 'slow-player', durationMs: 1500, slowMult: 0.5 });
      // 魔法彈鋪面:多顆波浪魔法彈散開,控場彈幕但速度慢留縫。
      out.push({ kind: 'magic-bolt', count: 6, speedPxPerSec: 190, amplitudePx: 52, periodMs: 850, damage: 9 });
      // phase2 新招:符陣寬扇 (fan-shot) — 中央 [-0.15,0.15] 留縫,速度慢 (260) 易閃。
      out.push({ kind: 'fan-shot', angles: [-0.7, -0.5, -0.3, 0.3, 0.5, 0.7], speedPxPerSec: 260, damage: 9 });
      if (self.berserkMode) {
        // berserk 收尾:進階環形 — 雙層 + 缺口 + 緩慢旋轉 (老闆「ring 太簡單一眼看穿」)。
        // 看穿變難 (要找對縫) 但缺口恆在 → 仍可躲 (K-3 鐵則)。速度維持 150。
        out.push({
          kind: 'ring-burst', count: 20, speedPxPerSec: 150, damage: 8,
          layers: RING_BERSERK_LAYERS, gapRad: RING_BERSERK_GAP_RAD, rotateDps: RING_BERSERK_ROTATE_DPS,
        });
      }
    }
    return out;
  },
};

const YUAN_SU: BossDef = {
  id: 'yuan-su',
  displayName: '元素守衛',
  hp: 900,
  speedPxPerSec: 50,
  // boss-behavior: 96→138 (×1.44).
  size: 138,
  color: 0x00897b,
  assetKey: ASSETS.bossYuanSu,
  shape: 'hexagon',
  faction: 'elemental',
  affinityFaction: 'elemental', // 元素守衛 = elemental guardian = elemental
  attackACooldownMs: 4000,
  attackBCooldownMs: 4500,
  phase2HpThreshold: 360,
  attackBWarningMs: 800,
  introDialog: '六元同源,你選哪一個?',
  arenaTint: 0x0a2a26,
  entranceCinematic: 'teleport-in',
  deathCinematic: 'fade-into-data',
  uniqueGimmick: 'teleport',
  tickAttacks(self: Boss, nowMs: number, _playerX: number): BossAttack[] {
    // boss-movesets 差異化 (老闆「每一魔王招式要不一樣」):
    //   YUAN_SU 元素守衛 = 自然/元素持續 → 主軸【凍結 + 魔法 + 持續環形/追蹤波】。
    //   phase1: 短凍結 + 魔法彈;phase2: 魔法 + 追蹤波;berserk: 持續環形 (元素壓力)。
    //   不放雷射 (SHU_MO 招牌)、不放緩速 (FU_HUN 招牌) → 跟另兩隻明顯不同。
    //   獨佔招式: freeze-player (凍結,另兩隻不用) + 魔法/追蹤組合。
    //   凍結很短 (250ms) 維持可玩性。純遊戲機制 (學習與遊戲分離)。
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;
    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      // 短凍結 (250ms) + 波浪魔法彈 (元素感) — 不再用直線單發,跟 SHU_MO 區隔。
      out.push({ kind: 'freeze-player', durationMs: 250 });
      out.push({ kind: 'magic-bolt', count: 3, speedPxPerSec: 210, amplitudePx: 40, periodMs: 950, damage: 9 });
    }
    if (self.phase === 2 && nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;
      // phase2 元素持續:魔法彈鋪面 + 追蹤波 (綿延壓力,可閃)。
      // boss-phase2-transform (老闆「攻擊方式要改變 動態變換」):phase2 質變 —
      //   homing-shot (追蹤波) 與 fan-shot (元素散射) 都是 phase1 沒有的招式,
      //   讓元素守衛半血後從「凍結+直魔法」變成「追蹤+散射」的持續壓力打法。
      out.push({ kind: 'magic-bolt', count: 5, speedPxPerSec: 200, amplitudePx: 48, periodMs: 880, damage: 9 });
      out.push({ kind: 'homing-shot', count: 3, speedPxPerSec: 220, turnRatePerSec: 115, damage: 9 });
      // phase2 新招:元素散射 (fan-shot) — 中央 [-0.2,0.2] 留縫,速度慢 (250) 可閃。
      out.push({ kind: 'fan-shot', angles: [-0.75, -0.55, -0.35, 0.35, 0.55, 0.75], speedPxPerSec: 250, damage: 8 });
      if (self.berserkMode) {
        // berserk:進階環形 — 雙層 + 缺口 + 緩慢旋轉 (老闆「ring 太簡單一眼看穿」)。
        // 元素守衛的招牌持續壓力,看穿變難但缺口恆在 → 仍可躲。速度維持 160。
        out.push({
          kind: 'ring-burst', count: 20, speedPxPerSec: 160, damage: 8,
          layers: RING_BERSERK_LAYERS, gapRad: RING_BERSERK_GAP_RAD, rotateDps: RING_BERSERK_ROTATE_DPS,
        });
      }
    }
    return out;
  },
};

export const BOSS_POOL: BossDef[] = [SHU_MO, FU_HUN, YUAN_SU];

/**
 * boss-roster-extra (boss 2026-06-13「boss 寫太少」): 自由戰 boss 波的「會遭遇」
 * 魔王全集 = 原 3 隻 tier1 + 6 隻新正式魔王 (BOSS_EXTRA_POOL)。pickRandomBoss
 * 從這個合併池抽,玩家在自由戰 boss 波就真的會輪到新魔王 (總可遭遇數 3 → 9)。
 * BOSS_POOL 本身保持 3 隻不變 (tier 契約 / fsm-parity 鏡射只認原 9 隻)。
 */
export const FREE_BATTLE_BOSS_POOL: BossDef[] = [...BOSS_POOL, ...BOSS_EXTRA_POOL];

export function pickRandomBoss(seedRandom: () => number = Math.random): BossDef {
  return FREE_BATTLE_BOSS_POOL[Math.floor(seedRandom() * FREE_BATTLE_BOSS_POOL.length)];
}

/**
 * W7 hub→battle wiring — lookup helper used by `HorizontalBattleScene.init(data)`
 * when the scene was started from `HubScene.onBuildingClick(factionId)`.
 *
 * Strategy:
 *   1. exact-id match across BOSS_POOL + TIER2_BOSS_POOL + TIER3_BOSS_POOL (so
 *      callers can pass any boss id, e.g. `'shu-mo'` / `'shu-fu'` / `'shi-zhong'`,
 *      or a faction id like `'celestial'`). R-004 D3:闖關第 3 章 boss 關帶
 *      tier3 bossId('shi-zhong'),先前只查 tier1/2 會 miss → 退回隨機 tier1。
 *   2. if the param matches a FactionId, return the first boss in BOSS_POOL
 *      whose `faction` field equals the param. Falls back to TIER2_BOSS_POOL
 *      for factions BOSS_POOL doesn't cover (techno / dominion currently).
 *   3. returns `null` when no match — caller falls back to `pickRandomBoss()`.
 *
 * Kept side-effect-free so unit tests can call it without instantiating Phaser.
 */
export function pickBossById(idOrFaction: string): BossDef | null {
  // 1. exact-id match across all pools (incl. boss-roster-extra 6 新魔王)
  const exact = BOSS_POOL.find(b => b.id === idOrFaction)
    ?? TIER2_BOSS_POOL.find(b => b.id === idOrFaction)
    ?? BOSS_TIER3_POOL.find(b => b.id === idOrFaction)
    ?? BOSS_EXTRA_POOL.find(b => b.id === idOrFaction);
  if (exact) return exact;
  // 2. faction match — first in BOSS_POOL, fallback TIER2_BOSS_POOL
  const byFactionTier1 = BOSS_POOL.find(b => b.faction === idOrFaction);
  if (byFactionTier1) return byFactionTier1;
  const byFactionTier2 = TIER2_BOSS_POOL.find(b => b.faction === idOrFaction);
  if (byFactionTier2) return byFactionTier2;
  return null;
}

/**
 * boss-roster-extra: tier-2 隨機池 = 原 4 隻 tier2 + 6 隻新正式魔王 (它們皆 tier:2),
 * 讓 tier-2 boss 波也輪得到新魔王。TIER2_BOSS_POOL 本身不變 (其專屬測試/契約只認原 4 隻)。
 */
const TIER2_RANDOM_POOL: BossDef[] = [...TIER2_BOSS_POOL, ...BOSS_EXTRA_POOL];

export function pickRandomBossByTier(tier: 1 | 2 | 3, rand: () => number = Math.random): BossDef {
  if (tier === 1) return pickRandomBoss(rand);
  if (tier === 2) return TIER2_RANDOM_POOL[Math.floor(rand() * TIER2_RANDOM_POOL.length)];
  // R-004 D3:tier3 池(期中/期末 boss)已實作,闖關第 3 章用之。
  return BOSS_TIER3_POOL[Math.floor(rand() * BOSS_TIER3_POOL.length)];
}